import EntryBlock from "./EntryBlock";
import { SNAP_MIN, HOURS, entryColor, hexA } from "./utils";

/**
 * A single day's column inside the WeekCalendar grid. Handles drag-to-create
 * (forwarded via `onBeginDrag`), renders entry blocks and the running-entry
 * tentative block, plus the current-time indicator and working-hour shading.
 *
 * `hourPx` — current pixels-per-hour scale (zoom). All vertical maths
 * (block top/height, hour lines, working-hour shading, now-line) use this.
 */
export default function DayColumn({
  day, entries, clientMap, activityMap, tagMap,
  onBeginDrag, dragPreview, onEntryClick, onEntryUpdate, onEntryResume,
  isLast, isToday, now, runningEntry, workHours, isWorkDay = true, hourPx,
}) {
  const nowMin = isToday ? (now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60) : null;
  const nowTop = nowMin != null ? (nowMin / 60) * hourPx : null;

  let runningBlock = null;
  if (runningEntry && !runningEntry.end) {
    const start = new Date(runningEntry.start);
    const endTime = isToday ? now : new Date(start);
    if (!isToday) endTime.setHours(23, 59, 59, 999);
    const startMin = start.getHours() * 60 + start.getMinutes() + start.getSeconds() / 60;
    const endMin = endTime.getHours() * 60 + endTime.getMinutes() + endTime.getSeconds() / 60;
    const top = (startMin / 60) * hourPx;
    const height = Math.max(22, ((endMin - startMin) / 60) * hourPx);
    const runClientIds = Array.isArray(runningEntry.client_ids) && runningEntry.client_ids.length
      ? runningEntry.client_ids
      : (runningEntry.client_id ? [runningEntry.client_id] : []);
    const runClients = runClientIds.map((id) => clientMap[id]).filter(Boolean);
    const primaryClient = runClients[0] || null;
    const activity = runningEntry.activity_id ? activityMap[runningEntry.activity_id] : null;
    const primaryCategory = Array.isArray(runningEntry.tag_ids) && runningEntry.tag_ids.length
      ? tagMap[runningEntry.tag_ids[0]]
      : null;
    const color = primaryCategory?.color || primaryClient?.color || activity?.color || "#145c8c";
    const elapsed = Math.max(0, Math.floor((endTime - start) / 1000));
    const hh = Math.floor(elapsed / 3600);
    const mm = Math.floor((elapsed % 3600) / 60);
    const ss = elapsed % 60;
    runningBlock = (
      <div
        className="absolute left-1 right-1 rounded-md px-2 py-1 text-xs overflow-hidden z-10 border border-dashed pointer-events-none"
        style={{
          top,
          height,
          borderColor: color,
          color: "#0b2c47",
          backgroundColor: "#ffffff",
          backgroundImage: `repeating-linear-gradient(135deg, ${hexA(color, 0.18)} 0 8px, transparent 8px 16px)`,
        }}
        data-testid="running-entry-block"
      >
        <div className="font-medium truncate">
          {runningEntry.description || "(running…)"}
        </div>
        {height > 36 && (
          <div className="flex items-center gap-1.5 text-[10px] mt-0.5 opacity-80">
            {runClients.length > 0 && (
              <span className="truncate">
                {runClients.length === 1 ? runClients[0].name : `${runClients[0].name} +${runClients.length - 1}`}
              </span>
            )}
            {runClients.length > 0 && activity && <span>·</span>}
            {activity && <span className="truncate">{activity.name}</span>}
          </div>
        )}
        <div className="timer-font text-[11px] mt-1 font-medium">
          {hh}:{String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative ${isLast ? "" : "border-r border-slate-100"} cursor-crosshair select-none ${
        isWorkDay ? "hover:bg-slate-50/30" : "bg-slate-100/60 hover:bg-slate-100/70"
      }`}
      style={{ height: hourPx * 24 }}
      onMouseDown={onBeginDrag}
      data-testid={`day-column-${day.toISOString().slice(0, 10)}`}
      data-non-work-day={!isWorkDay ? "true" : undefined}
    >
      {workHours && (
        <>
          {workHours.startMin > 0 && (
            <div className="absolute left-0 right-0 pointer-events-none bg-slate-100/35"
                 style={{ top: 0, height: (workHours.startMin / 60) * hourPx }} />
          )}
          {workHours.endMin < 24 * 60 && (
            <div className="absolute left-0 right-0 pointer-events-none bg-slate-100/35"
                 style={{ top: (workHours.endMin / 60) * hourPx,
                          height: ((24 * 60 - workHours.endMin) / 60) * hourPx }} />
          )}
          {workHours.lunchStartMin != null && workHours.lunchEndMin != null
            && workHours.lunchEndMin > workHours.lunchStartMin && (
            <div className="absolute left-0 right-0 pointer-events-none bg-slate-100/25"
                 style={{ top: (workHours.lunchStartMin / 60) * hourPx,
                          height: ((workHours.lunchEndMin - workHours.lunchStartMin) / 60) * hourPx }} />
          )}
        </>
      )}

      {HOURS.map((h) => (
        <div key={h.h} className="absolute left-0 right-0 border-t border-slate-100 pointer-events-none"
             style={{ top: h.h * hourPx }} />
      ))}

      {runningBlock}

      {nowTop != null && (
        <div className="absolute left-0 right-0 pointer-events-none z-30"
             style={{ top: nowTop - 1 }}
             data-testid="current-time-line">
          <div className="absolute left-0 -translate-x-1/2 -translate-y-1/2 top-1/2 h-3.5 w-3.5 rounded-full bg-[#145c8c] shadow-md ring-2 ring-white" />
          <div className="h-0.5 bg-[#145c8c]" />
        </div>
      )}

      {dragPreview && (() => {
        const a = Math.min(dragPreview.startMin, dragPreview.endMin);
        const b = Math.max(dragPreview.startMin, dragPreview.endMin);
        const span = Math.max(SNAP_MIN, b - a);
        const top = (a / 60) * hourPx;
        const height = (span / 60) * hourPx;
        const totalSec = span * 60;
        const hh = Math.floor(totalSec / 3600);
        const mm = Math.floor((totalSec % 3600) / 60);
        const ss = totalSec % 60;
        return (
          <div
            className="absolute left-1 right-1 rounded-md pointer-events-none z-10"
            style={{
              top,
              height,
              background: "rgba(148, 163, 184, 0.35)",
              border: "1px dashed rgba(71, 85, 105, 0.4)",
            }}
            data-testid="drag-preview"
          >
            <div className="absolute bottom-1 left-2 timer-font text-[11px] font-medium text-slate-700">
              {hh}:{String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")}
            </div>
          </div>
        );
      })()}

      {entries.map((e) => {
        const entryClientIds = Array.isArray(e.client_ids) && e.client_ids.length
          ? e.client_ids
          : (e.client_id ? [e.client_id] : []);
        const entryClients = entryClientIds.map((id) => clientMap[id]).filter(Boolean);
        const primaryClient = entryClients[0] || null;
        const activity = e.activity_id ? activityMap[e.activity_id] : null;
        const primaryCategory = Array.isArray(e.tag_ids) && e.tag_ids.length ? tagMap[e.tag_ids[0]] : null;
        const color = entryColor(e, primaryClient, activity, primaryCategory);
        return (
          <EntryBlock
            key={e.id}
            entry={e}
            color={color}
            clients={entryClients}
            activity={activity}
            tagMap={tagMap}
            onClickEntry={() => onEntryClick(e)}
            onUpdate={onEntryUpdate}
            onResume={onEntryResume}
            hourPx={hourPx}
          />
        );
      })}
    </div>
  );
}
